import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Check, Clock, Target, Zap } from 'lucide-react';
import simulationRunService from '../../services/simulationRunService';
import useWebSocket from '../../hooks/useWebSocket';
import useCountdown, { formatClock } from '../../hooks/useCountdown';
import FinalDebrief from '../../components/simulation/FinalDebrief';
import {
  movementSense, formatDelta, kpiBarState, metricLevel, formatTarget, targetStatus,
} from '../../utils/simulationEngine';

/**
 * PROJECTOR — the shared screen at the front of the room.
 *
 * Two rules shape everything here:
 *   1. It renders a payload that CANNOT contain a pre-reveal choice. The safety is in
 *      the serializer (simulationSerializer.forProjector), not in this file choosing
 *      not to render — so a future edit here cannot put the answer key on a wall.
 *   2. No controls. The trainer drives from the console on their own laptop; a stray
 *      click on the projected screen must be incapable of changing the run.
 *
 * Deliberately outside DashboardLayout: no sidebar, no nav, no chrome. Type is sized
 * to be read from the back of a training room, not from a desk.
 */

const PHASE_TITLE = {
  lobby: 'Getting ready',
  assign: 'Finding your teams',
  brief: 'The situation',
  negotiate: 'Talk to each other',
  decide: 'Decide',
  reveal: 'What everyone chose',
  debrief: 'So what happened?',
  closed: 'Final debrief',
};

const SimulationProjectorPage = () => {
  const { runId } = useParams();
  const [p, setP] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const socket = useWebSocket(sessionId);
  const { on, off, emit, isConnected } = socket;

  const load = useCallback(async () => {
    try {
      const res = await simulationRunService.projector(runId);
      setP(res.projector);
    } catch {
      /* transient — the socket will prompt another fetch */
    }
  }, [runId]);

  useEffect(() => { load(); }, [load]);

  // The run id is enough to subscribe; the session id only scopes the shared socket.
  useEffect(() => {
    (async () => {
      try {
        const res = await simulationRunService.get(runId);
        setSessionId(res.run?.sessionId || null);
      } catch { /* ignore */ }
    })();
  }, [runId]);

  useEffect(() => { if (isConnected) emit('sim:watch', { runId }); }, [isConnected, emit, runId]);

  // Every server-side change refetches rather than merging a partial payload — the
  // projector is a mirror, and a mirror that reconstructs state can drift.
  useEffect(() => {
    const refetch = () => load();
    on('sim:phase-changed', refetch);
    on('sim:submission-status', refetch);
    on('sim:reveal', refetch);
    on('sim:ended', refetch);
    on('sim:final-view', refetch);
    return () => {
      off('sim:phase-changed', refetch);
      off('sim:submission-status', refetch);
      off('sim:reveal', refetch);
      off('sim:ended', refetch);
      off('sim:final-view', refetch);
    };
  }, [on, off, load]);

  const timed = ['negotiate', 'decide'].includes(p?.phase);
  const secondsLeft = useCountdown(timed ? p?.phaseEndsAt : null, p?.serverNow);

  if (!p) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-950">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-neutral-800 border-t-primary-500" />
      </div>
    );
  }

  const locked = p.lockStatus?.lockedFunctionKeys || [];

  return (
    <div className="min-h-screen bg-neutral-950 px-10 py-8 text-white">
      {/* header */}
      <div className="mb-8 flex items-baseline justify-between gap-6 border-b border-neutral-800 pb-5">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary-400">
            {PHASE_TITLE[p.phase] || 'Simulation'}
          </p>
          {/* text-white is explicit: a global rule colours every h1-h6 dark navy,
              which beats inheritance and left this title near-invisible on black. */}
          <h1 className="mt-1 text-4xl font-bold leading-tight text-white">
            {p.phase === 'closed' ? p.meta?.title : p.round?.title || p.meta?.title}
          </h1>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {/* The countdown. Running out says "wrap up" — the trainer still moves on. */}
          {typeof secondsLeft === 'number' && (
            <span
              className={`rounded-xl px-5 py-2 text-3xl font-bold tabular-nums ${
                secondsLeft === 0 ? 'bg-red-500/15 text-red-400'
                  : secondsLeft <= 30 ? 'bg-amber-500/15 text-amber-400'
                  : 'bg-neutral-900 text-white'
              }`}
            >
              {secondsLeft === 0 ? "Time's up" : formatClock(secondsLeft)}
            </span>
          )}
          {p.phase !== 'closed' && (
            <span className="rounded-xl bg-neutral-900 px-4 py-2 text-lg font-semibold tabular-nums text-neutral-400">
              Round {p.roundIndex} / {p.roundCount}
            </span>
          )}
        </div>
      </div>

      {/* ---------- BRIEF / NEGOTIATE ---------- */}
      {['brief', 'negotiate', 'assign', 'lobby'].includes(p.phase) && p.round && (
        <div className="grid gap-8 lg:grid-cols-[3fr_2fr]">
          <div>
            <p className="text-2xl leading-relaxed text-neutral-200">{p.round.context}</p>

            {p.round.conflict?.length > 0 && (
              <div className="mt-8 space-y-3">
                {p.round.conflict.map((line, i) => (
                  <div key={i} className="rounded-2xl bg-neutral-900 px-6 py-4">
                    <span className="text-sm font-bold uppercase tracking-wider text-primary-400">
                      {line.speakerLabel}
                    </span>
                    <span className="text-sm text-neutral-500"> {line.verb}</span>
                    <p className="mt-1 text-xl text-neutral-100">&ldquo;{line.statement}&rdquo;</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="rounded-2xl border-2 border-primary-500/40 bg-primary-500/10 px-6 py-5">
              <p className="text-sm font-bold uppercase tracking-wider text-primary-400">The call</p>
              <p className="mt-2 text-2xl font-semibold leading-snug text-white">{p.round.challenge}</p>
            </div>

            {p.round.decisionTensions?.length > 0 && (
              <ul className="mt-6 space-y-3">
                {p.round.decisionTensions.map((t) => (
                  <li key={t} className="flex gap-3 text-lg text-neutral-300">
                    <span className="mt-2.5 h-2 w-2 shrink-0 rounded-full bg-neutral-600" />
                    {t}
                  </li>
                ))}
              </ul>
            )}

            {p.phase === 'negotiate' && (
              <div className="mt-8 rounded-2xl border-2 border-dashed border-neutral-700 px-6 py-8 text-center">
                <Clock size={28} strokeWidth={1.75} className="mx-auto text-primary-400" />
                <p className="mt-3 text-2xl font-bold text-white">Go and talk to the other teams</p>
                <p className="mt-1 text-lg text-neutral-400">Before anyone commits.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---------- DECIDE: counts only, never choices ---------- */}
      {p.phase === 'decide' && (
        <div>
          <p className="text-2xl text-neutral-300">Each team is choosing. Nobody sees anyone else&apos;s call yet.</p>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {p.functions.map((fn) => {
              const isIn = locked.includes(fn.key);
              return (
                <div
                  key={fn.key}
                  className={`rounded-2xl border-2 px-6 py-8 text-center transition-colors ${
                    isIn ? 'border-emerald-500/50 bg-emerald-500/10' : 'border-neutral-800 bg-neutral-900'
                  }`}
                >
                  <p className="text-2xl font-bold text-white">{fn.name}</p>
                  <p className="mt-1 text-sm text-neutral-500">{fn.mandate}</p>
                  <div className="mt-5">
                    {isIn ? (
                      <span className="inline-flex items-center gap-2 text-lg font-bold text-emerald-400">
                        <Check size={20} strokeWidth={3} />Locked in
                      </span>
                    ) : (
                      <span className="text-lg text-neutral-500">Deciding…</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="mt-10 text-center text-3xl font-bold tabular-nums text-neutral-400">
            {locked.length} of {p.lockStatus.teamCount} teams ready
          </p>
        </div>
      )}

      {/* ---------- REVEAL / DEBRIEF ---------- */}
      {/* The insight on each choice is deliberately absent here: the room hears it in
          the final debrief, after the last round. */}
      {['reveal', 'debrief'].includes(p.phase) && p.reveal && (
        <div className="space-y-8">
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {p.reveal.attribution.map((a) => (
              <div key={a.functionKey} className="rounded-2xl border-2 border-neutral-800 bg-neutral-900 px-6 py-5">
                <p className="text-sm font-bold uppercase tracking-wider text-primary-400">{a.functionName}</p>
                <p className="mt-2 text-xl font-bold leading-snug text-white">{a.card?.title}</p>
                <p className="mt-1.5 text-base leading-relaxed text-neutral-400">{a.card?.description}</p>
              </div>
            ))}
          </div>

          {p.reveal.event && (
            <div className="rounded-2xl border-2 border-amber-500/40 bg-amber-500/10 px-6 py-5">
              <div className="flex items-start gap-4">
                <Zap size={26} strokeWidth={1.75} className="mt-0.5 shrink-0 text-amber-400" />
                <div>
                  <p className="text-sm font-bold uppercase tracking-wider text-amber-400">{p.meta.eventDeckLabel}</p>
                  <p className="mt-1 text-2xl font-bold text-white">{p.reveal.event.title}</p>
                  <p className="mt-1 text-lg text-neutral-300">{p.reveal.event.description}</p>
                </div>
              </div>
            </div>
          )}

          {/* KPI movement, big */}
          <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {p.kpis.map((kpi) => {
              const delta = p.reveal.total?.[kpi.key];
              // Cumulative is change since the start; the room reads the level.
              const change = p.cumulative?.[kpi.key] ?? 0;
              const value = metricLevel(kpi, change);
              const sense = movementSense(kpi, delta);
              const target = formatTarget(kpi);
              const status = targetStatus(kpi, change);
              // Same clamp problem as KpiStrip, and it matters more here: this is the
              // screen the whole room reads from the back.
              const { pct, overCap, belowFloor } = kpiBarState(kpi, change);
              return (
                <div key={kpi.key} className="rounded-2xl bg-neutral-900 px-5 py-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{kpi.label}</p>
                  <div className="mt-1 flex items-baseline gap-3">
                    <span className="text-3xl font-bold tabular-nums">{value}</span>
                    <span className={`text-lg font-bold tabular-nums ${
                      sense === 'good' ? 'text-emerald-400' : sense === 'bad' ? 'text-red-400' : 'text-neutral-500'}`}>
                      {formatDelta(delta)}
                    </span>
                  </div>
                  <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-neutral-800">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${
                        belowFloor ? 'bg-red-500'
                          : pct >= 80 ? 'bg-red-500'
                          : pct >= 50 ? 'bg-amber-500'
                          : 'bg-emerald-500'}`}
                      style={{ width: `${belowFloor ? 100 : pct}%` }}
                    />
                  </div>
                  {(overCap || belowFloor) && (
                    <p className="mt-1.5 text-[11px] font-semibold text-red-400">
                      {Number(kpi.start)
                        ? (overCap ? `off the scale — moved more than ${kpi.cap} from the start` : 'off the scale — below where it started')
                        : (overCap ? `off the scale — past ${kpi.cap}` : 'off the scale — below zero')}
                    </p>
                  )}
                  {target && (
                    <p className={`mt-2 inline-flex items-center gap-1 text-sm font-semibold ${
                      status === 'met' ? 'text-emerald-400' : 'text-amber-400'}`}
                    >
                      {status === 'met' ? <Check size={14} strokeWidth={3} /> : <Target size={14} strokeWidth={2} />}
                      Target {target}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ---------- FINAL DEBRIEF: after the last round ---------- */}
      {p.phase === 'closed' && (
        <FinalDebrief final={p.final} kpis={p.kpis} view={p.finalView} />
      )}
    </div>
  );
};

export default SimulationProjectorPage;
